import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

// Match the backend's email validator pattern
export const EMAIL_PATTERN =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,7}$/;

// Match the backend's password validator pattern
export const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d\s]).*$/;

export class CustomValidators {
  /**
   * Custom email validator that uses the same regex pattern as the backend
   * @returns ValidatorFn that checks if the email matches the pattern
   */
  static email(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const isValid = EMAIL_PATTERN.test(control.value);
      return isValid ? null : { email: true };
    };
  }

  /**
   * Custom password validator that uses the same regex pattern as the backend
   * @returns ValidatorFn that checks if the password meets the strength requirements
   */
  static strongPassword(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }

      const isValid = PASSWORD_PATTERN.test(control.value);
      if (!isValid) {
        return {
          strongPassword: true,
          message:
            "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
        };
      }
      return null;
    };
  }

  /**
   * Checks if two fields have matching values
   * @param controlName The name of the first control to compare
   * @param matchingControlName The name of the second control to compare
   * @returns ValidatorFn that adds passwordMismatch error if values don't match
   */
  static matchValues(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const control = formGroup.get(controlName);
      const matchingControl = formGroup.get(matchingControlName);

      if (!control || !matchingControl) {
        return null;
      }

      if (matchingControl.errors && !matchingControl.errors["mustMatch"]) {
        // Return if another validator has already found an error on the matchingControl
        return null;
      }

      // Set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
        return { mustMatch: true };
      } else {
        matchingControl.setErrors(null);
        return null;
      }
    };
  }

  static passwordMatcher(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const passwordControl = formGroup.get("password");
      const confirmPasswordControl = formGroup.get("confirmPassword");

      if (!passwordControl || !confirmPasswordControl) {
        return null;
      }

      const password = passwordControl.value;
      const confirmPassword = confirmPasswordControl.value;

      if (password !== confirmPassword) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      } else {
        if (confirmPasswordControl.errors) {
          const errors = { ...confirmPasswordControl.errors };
          delete errors["passwordMismatch"];
          confirmPasswordControl.setErrors(Object.keys(errors).length ? errors : null);
        }
        return null;
      }
    };
  }
}
